import { useState } from "react";

import type { ConfigSummary } from "../api/types.gen";
import { BenchmarkChart, ChartFoot } from "./BenchmarkChart";
import type { ChartKind, Metric, ValueField } from "./BenchmarkChart";

/**
 * The section-and-tab structure of the results screen.
 *
 * A port of the `views` table in `design-prototype/dashboard.js`, kept as data
 * for the same reason it was data there: a section is a heading, an intro and a
 * list of tabs, and expressing that as markup would make adding a chart a
 * component instead of a row.
 *
 * Every listed tab is backed by a real configuration-level rollup. Features
 * that need data the service does not persist are omitted rather than shown as
 * locked placeholders; trajectory analysis is the dashboard's only gated
 * feature.
 */

/** One chart within a section. */
export interface ChartTab {
  id: string;
  /** The tab strip's short label. */
  label: string;
  /** The heading over the chart itself. */
  title: string;
  description: string;
  kind: ChartKind;
  metric: Metric;
  field: ValueField;
  /** The x-axis title, for a scatter. */
  xLabel?: string;
  /**
   * A scatter's y axis, where it is not the index. Supplying it also drops the
   * quadrant wash: shading a corner green is a verdict, and there is no better
   * corner on a plot of input against output tokens.
   */
  yField?: ValueField;
  yMetric?: Metric;
  yLabel?: string;
}

export interface ChartSectionSpec {
  id: string;
  heading: string;
  intro: string;
  tabs: ChartTab[];
}

/**
 * Every section of the dashboard, in the order they are rendered.
 *
 * Every chart reads only fields `ConfigSummary` actually carries.
 */
export const SECTIONS: ChartSectionSpec[] = [
  {
    id: "performance",
    heading: "Performance",
    intro:
      "Pass@1 across the repository challenge set. Attempts are normalized within each challenge so every challenge has equal weight.",
    tabs: [
      {
        id: "index",
        label: "BER Index",
        title: "BER Index",
        description: "Pass@1 averaged across measured challenges · Higher is better",
        kind: "bar",
        metric: "index",
        field: "agent_index",
      },
    ],
  },
  {
    id: "tokens",
    heading: "Token usage",
    intro:
      "Token consumption across valid runs, including prompt input, cached input, generated output, and task-normalized efficiency.",
    tabs: [
      {
        id: "token-usage",
        label: "Token usage",
        title: "Token Usage per Challenge",
        description: "Average input, cached input, and output tokens per valid challenge",
        kind: "stacked",
        metric: "tokens",
        field: "mean_total_tokens",
      },
      {
        id: "cache-hit",
        // "Read share", not "hit rate", everywhere the reader can see it. The
        // agents do not report cache CREATION tokens to this service, so what
        // is measurable is how much of the prompt came from cache — a run that
        // wrote a large cache and read none of it back looks the same here as
        // a run with no cache. The chart foot says so in full.
        label: "Prompt cache read share",
        title: "Prompt Cache Read Share",
        description:
          "Share of each run's prompt served from cache rather than sent again · Averaged per run",
        kind: "bar",
        metric: "ratio",
        field: "mean_cache_hit_rate",
      },
      {
        id: "context-window",
        label: "Context window size",
        title: "Context Window Utilization",
        description:
          "Peak context as a share of the model's window · Averaged per run · Runs whose tool published no window are excluded",
        kind: "bar",
        metric: "ratio",
        field: "mean_context_pct",
      },
      {
        id: "input-output",
        label: "Input vs output",
        title: "Input vs. Output Tokens",
        // Not "four-quadrant" any more. The quadrants are drawn but nothing is
        // shaded, because neither axis has a good direction: reading little
        // and writing little is either efficiency or having given up, and the
        // measurement cannot tell them apart.
        description: "Average prompt volume against generated output, per measured challenge.",
        kind: "scatter",
        metric: "tokens",
        field: "mean_input_tokens",
        xLabel: "Input tokens per challenge",
        yField: "mean_output_tokens",
        yMetric: "tokens",
        yLabel: "Output tokens per challenge",
      },
      {
        id: "index-tokens",
        label: "Index vs total tokens",
        title: "BER Index vs. Total Tokens",
        description:
          "Higher and farther left indicates stronger results with fewer total tokens.",
        kind: "scatter",
        metric: "tokens",
        field: "mean_total_tokens",
        xLabel: "Total tokens per challenge",
      },
    ],
  },
  {
    id: "cost",
    heading: "Cost",
    intro:
      "Average agent-reported spend per valid challenge. Missing and errored measurements are excluded rather than counted as zero.",
    tabs: [
      {
        id: "cost-average",
        label: "Cost per challenge",
        title: "Cost per Challenge",
        description: "Average agent-reported USD per valid challenge · Lower is better",
        kind: "bar",
        metric: "cost",
        field: "mean_reported_cost_usd",
      },
      {
        id: "index-cost",
        label: "Index vs cost",
        title: "BER Index vs. Cost per Challenge",
        description:
          "Higher and farther left indicates better performance at lower reported cost.",
        kind: "scatter",
        metric: "cost",
        field: "mean_reported_cost_usd",
        xLabel: "Reported cost per challenge (USD)",
      },
    ],
  },
  {
    id: "time",
    heading: "Execution time",
    intro:
      "Active agent wall time per valid challenge. Environment startup, verification, and adapter errors are excluded.",
    tabs: [
      {
        id: "time-average",
        label: "Time per challenge",
        title: "Time per Challenge",
        description: "Average active agent time per valid challenge · Lower is better",
        kind: "bar",
        metric: "time",
        field: "mean_agent_seconds",
      },
      {
        id: "index-time",
        label: "Index vs time",
        title: "BER Index vs. Execution Time",
        description:
          "Higher and farther left indicates stronger results in less active time.",
        kind: "scatter",
        metric: "time",
        field: "mean_agent_seconds",
        xLabel: "Active time per challenge (seconds)",
      },
    ],
  },
  {
    id: "code",
    heading: "Code",
    intro:
      "How agent-generated patches compare with the accepted reference patch, including patch size, spread, and interaction depth.",
    tabs: [
      {
        id: "diff-spread",
        label: "AI vs gold diff",
        title: "AI vs. Gold Diff Spread",
        description: "Average changed lines in the agent patch compared with the accepted gold patch.",
        kind: "diff",
        metric: "diff",
        field: "mean_ai_changed_lines",
      },
      {
        id: "index-diff",
        label: "Index vs diff spread",
        title: "BER Index vs. Diff Spread",
        description:
          "Higher and farther left indicates stronger results with a tighter patch.",
        kind: "scatter",
        metric: "diff",
        field: "mean_ai_changed_lines",
        xLabel: "AI patch changed lines",
      },
      {
        id: "turns-average",
        label: "Number of turns",
        title: "Turns per Challenge",
        description: "Average number of model turns in a valid challenge run · Lower is leaner",
        kind: "bar",
        metric: "turns",
        field: "mean_turns",
      },
    ],
  },
];

/**
 * How each chart sorts its rows.
 *
 * The RANK is the server's and is never re-derived here; this is display order
 * within one chart, which is a different question — a cost chart that drew its
 * bars in index order would be unreadable. Unavailable rows sort last in every
 * case: a null is not a zero and must not lead a "lower is better" chart.
 */
function sortFor(rows: ConfigSummary[], tab: ChartTab): ConfigSummary[] {
  if (tab.field === undefined) return rows;
  if (tab.kind === "scatter") return rows;

  // Descending for the metrics where a larger bar is the better outcome, and
  // for the two shares — where neither direction is "better", but a chart
  // ordered smallest-first reads as a ranking of the worst.
  const descending = tab.metric === "index" || tab.metric === "ratio";
  return [...rows].sort((a, b) => {
    const av = a[tab.field as keyof ConfigSummary] as number | undefined;
    const bv = b[tab.field as keyof ConfigSummary] as number | undefined;
    if (descending) return (bv ?? -1) - (av ?? -1);
    return (av ?? Infinity) - (bv ?? Infinity);
  });
}

/**
 * One section: its heading, a tab strip, and one chart in the stage.
 *
 * The active tab is component state rather than a URL parameter. It is a view
 * of one dataset and not a destination — unlike the repository, which
 * `berbench upload` prints a link to and which therefore lives in the path.
 */
export function ChartTabs({
  section,
  rows,
}: {
  section: ChartSectionSpec;
  rows: ConfigSummary[];
}) {
  const [activeID, setActiveID] = useState(section.tabs[0].id);
  const active = section.tabs.find((tab) => tab.id === activeID) ?? section.tabs[0];

  return (
    <section className="analysis-section" id={section.id}>
      <div className="section-intro">
        <h2>{section.heading}</h2>
        <p>{section.intro}</p>
      </div>

      <div className="chart-shell">
        <div className="chart-tabs" role="tablist" aria-label={`${section.heading} chart views`}>
          {section.tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${section.id}-tab-${tab.id}`}
              aria-selected={tab.id === active.id}
              aria-controls={`${section.id}-panel`}
              className={tab.id === active.id ? "active" : undefined}
              onClick={() => setActiveID(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          className="chart-stage"
          id={`${section.id}-panel`}
          role="tabpanel"
          aria-labelledby={`${section.id}-tab-${active.id}`}
        >
          <div className="chart-topline">
            <div>
              <h3>{active.title}</h3>
              <p>{active.description}</p>
            </div>
            <div className="chart-tools">
              {/* The prototype's `Configuration | Agent` segmented control is
                  not here. It switched nothing, and a control that does
                  nothing teaches a reader that the controls do nothing. */}
              <span className="count-pill">{rows.filter((row) => row.coverage_state === "comparable").length} setups</span>
            </div>
          </div>

          <BenchmarkChart
            rows={sortFor(rows, active)}
            metric={active.metric}
            field={active.field}
            kind={active.kind}
            xLabel={active.xLabel}
            yField={active.yField}
            yMetric={active.yMetric}
            yLabel={active.yLabel}
            title={active.title}
          />
          <ChartFoot metric={active.metric} rows={rows} field={active.field} />
        </div>
      </div>
    </section>
  );
}
